import { coreApi } from '@shared/api/coreApi';
import { tenantApi } from '@shared/api/tenantApi';
import { unwrap } from '@shared/api/unwrap';

/**
 * Join-a-tenant + onboarding-gate API — mirrors the Angular member-portal
 * `JoinTenantService` and the onboarding gate the `OnboardingGuard` reads.
 *
 * SSO + tenant switching itself lives in `features/auth/api.ts`
 * (`/portal-auth/login`, `/portal-auth/switch-tenant`,
 * `/portal-auth/joinable-tenants`) — this module covers the *join* journey:
 * discover a community, request to join, then clear the onboarding gate.
 */

export interface TenantSearchResult {
  tenant_id: string;
  name: string;
  /** Member-portal hostname for Core resolve-domain + Tenant API signup. */
  resolution_domain: string | null;
  logo_small_url?: string | null;
  logo_large_url?: string | null;
}

export interface JoinRequestMemberData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  member_type_id?: string;
  /** Terms documents the member ticked, by id — validated against the target community's own set. */
  accepted_term_ids?: string[];
  id_number?: string;
  membership_number?: string;
  location_id?: string;
  property_residency_kind?: 'resident' | 'non_resident' | 'temporary_resident';
  property_relationship_type_id?: string;
}

export interface JoinRequestResult {
  member_request_id: string;
  onboarding_id?: string;
  flow_instance_id?: string;
  /** Opens that flow instance on the public flow routes to the person who just joined. */
  flow_instance_access_token?: string;
}

export interface JoinStatus {
  member_request_id: string;
  tenant_id: string;
  request_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

/**
 * The shape returned by both `GET /members/me/onboarding-gate` (post-login)
 * and `GET /public/member-requests/join/onboarding-gate` (join flow).
 */
export interface OnboardingGate {
  requires_onboarding: boolean;
  onboarding_status?: string | null;
  flow_instance_id?: string | null;
  onboarding_id?: string | null;
  flow_template_id?: string | null;
}

/**
 * Search joinable tenants within a root scope (Core API, public).
 *
 * `coreUserId` drops the communities this member already belongs to, so the list only
 * offers ones they can actually join — Core answers it from its own membership records,
 * which cover links made outside SSO too.
 */
export async function searchTenantsByRoot(
  rootTenantId: string,
  query?: string,
  coreUserId?: string,
): Promise<TenantSearchResult[]> {
  const { data } = await coreApi.get<{ tenants?: TenantSearchResult[] }>(
    '/tenants/search-by-root',
    {
      params: {
        root_tenant_id: rootTenantId,
        q: query?.trim() || undefined,
        core_user_id: coreUserId || undefined,
      },
    },
  );
  return data.tenants ?? [];
}

/**
 * The target community's domain-config id — the handle its terms documents hang off.
 * Fetched rather than read from `moduleStore`, whose config belongs to the community the
 * session is currently in, not the one being joined.
 */
export async function fetchJoinDomainConfigId(domain: string): Promise<string | undefined> {
  const { data } = await tenantApi.get('/public/domain-config', { params: { domain } });
  return unwrap<{ domainConfigId?: string }>(data)?.domainConfigId;
}

/** Submit a request to join a tenant (Tenant API, public). */
export async function joinTenant(
  coreUserId: string,
  memberData?: JoinRequestMemberData,
  rootTenantId?: string,
): Promise<JoinRequestResult> {
  const { data } = await tenantApi.post('/public/member-requests/join', {
    core_user_id: coreUserId,
    ...(memberData && Object.keys(memberData).length ? { member_data: memberData } : {}),
    ...(rootTenantId ? { root_tenant_id: rootTenantId } : {}),
  });
  return unwrap<JoinRequestResult>(data);
}

/** Latest join request for this Core user on the active tenant. */
export async function fetchJoinStatus(coreUserId: string): Promise<JoinStatus | null> {
  const { data } = await tenantApi.get('/public/member-requests/join/status', {
    params: { core_user_id: coreUserId },
  });
  return unwrap<JoinStatus | null>(data);
}

/**
 * Onboarding gate for a pending join request (pre tenant-context switch).
 *
 * `tenantId` addresses a community the app is not currently inside — the drawer's pending
 * entry asks about one while the session sits in another, and the ambient tenant would
 * answer for the wrong community.
 */
export async function fetchJoinOnboardingGate(
  coreUserId: string,
  tenantId?: string,
): Promise<OnboardingGate> {
  const { data } = await tenantApi.get('/public/member-requests/join/onboarding-gate', {
    params: { core_user_id: coreUserId },
    ...(tenantId ? { headers: { 'x-tenant-id': tenantId } } : {}),
  });
  return unwrap<OnboardingGate>(data);
}

/**
 * Post-login onboarding gate for the authenticated member. The
 * `OnboardingGuard` equivalent: if `requires_onboarding`, route the member
 * into `/onboarding` with the supplied flow/instance ids before letting
 * them into the app.
 */
export async function fetchMyOnboardingGate(): Promise<OnboardingGate> {
  const { data } = await tenantApi.get('/members/me/onboarding-gate');
  return unwrap<OnboardingGate>(data);
}

/**
 * The member has finished the flow and is waiting on the estate office. The
 * gate still withholds app access (approval is staff-driven), but there is
 * nothing left for them to fill in — sending them back into the runner would
 * just bounce them between a completed flow and the gate.
 */
export function isAwaitingOnboardingApproval(gate: OnboardingGate): boolean {
  return (
    gate.requires_onboarding &&
    (gate.onboarding_status === 'submitted' || gate.onboarding_status === 'under_review')
  );
}

/**
 * Build the onboarding route a gate result should redirect to. An onboarding the gate knows is opened
 * through its id, so the runner starts it, or resumes it with the token this session was handed for
 * it, and keeps the access token the public flow routes need; a bare instance id carries no token,
 * which matters when joining a community the session does not belong to.
 */
export function onboardingRouteForGate(gate: OnboardingGate): string | null {
  if (!gate.requires_onboarding) return null;
  if (gate.onboarding_id) {
    return gate.flow_template_id
      ? `/onboarding/${gate.flow_template_id}?onboardingId=${gate.onboarding_id}`
      : `/onboarding?onboardingId=${gate.onboarding_id}`;
  }
  if (gate.flow_instance_id) return `/onboarding?instance=${gate.flow_instance_id}`;
  if (gate.flow_template_id) return `/onboarding/${gate.flow_template_id}`;
  return '/onboarding';
}
